/**
 * Shared booking logic utilities
 */
import { parseTimeToMinutes } from "./TimeUtils";
import { BUSINESS_HOURS } from "../constants/TimeConstants";

/**
 * Filters time ranges by removing those that conflict with existing bookings
 */
export function filterTimeRangesByBookings(
    timeRanges: Array<{ key: string; text: string }>,
    bookings: any[],
    selectedDate: Date
): Array<{ key: string; text: string }> {
    if (!bookings || bookings.length === 0) {
        return timeRanges;
    }

    // Filter out time ranges that conflict with existing bookings for the same date
    const availableRanges = timeRanges.filter((range) => {
        const timeRange = range.key;
        if (!timeRange.includes("-")) {
            return true; // Keep non-standard ranges like "flexible-time"
        }

        const [startTime, endTime] = timeRange.split("-");
        const rangeStart = parseTimeToMinutes(startTime);
        const rangeEnd = parseTimeToMinutes(endTime);

        // Check if this time range conflicts with any existing booking
        const hasConflict = bookings.some((booking) => {
            if (booking.cancelled) {
                return false; // Ignore cancelled bookings
            }

            const bookingStart = new Date(booking.startTime);
            const bookingEnd = new Date(booking.endTime);
            const bookingDate = new Date(bookingStart);
            bookingDate.setHours(0, 0, 0, 0);

            const selectedDateNormalized = new Date(selectedDate);
            selectedDateNormalized.setHours(0, 0, 0, 0);

            // Only check bookings for the same date
            if (bookingDate.getTime() !== selectedDateNormalized.getTime()) {
                return false;
            }

            const bookingStartMinutes = bookingStart.getHours() * 60 + bookingStart.getMinutes();
            const bookingEndMinutes = bookingEnd.getHours() * 60 + bookingEnd.getMinutes();

            // Check if the ranges overlap
            return !(rangeEnd <= bookingStartMinutes || rangeStart >= bookingEndMinutes);
        });

        return !hasConflict;
    });

    // If no ranges are available, add a message range
    if (availableRanges.length === 0) {
        return [{ key: "no-time-available", text: "No time slots available" }];
    }

    return availableRanges;
}

/**
 * Checks if a seat is fully booked for the day (no available time slots)
 */
export function isSeatFullyBookedForDay(bookings: any[], selectedDate: Date): boolean {
    if (!bookings || bookings.length === 0) {
        return false;
    }

    // Filter out cancelled bookings and bookings for different dates
    const validBookings = bookings.filter((booking) => {
        if (booking.cancelled) {
            return false;
        }

        const bookingStart = new Date(booking.startTime);
        const bookingDate = new Date(bookingStart);
        bookingDate.setHours(0, 0, 0, 0);

        const selectedDateNormalized = new Date(selectedDate);
        selectedDateNormalized.setHours(0, 0, 0, 0);

        return bookingDate.getTime() === selectedDateNormalized.getTime();
    });

    if (validBookings.length === 0) {
        return false;
    }

    // Define business time slots (morning and afternoon, excluding lunch break)
    const morningStart = parseTimeToMinutes(BUSINESS_HOURS.START);
    const morningEnd = parseTimeToMinutes(BUSINESS_HOURS.LUNCH_START);
    const afternoonStart = parseTimeToMinutes(BUSINESS_HOURS.LUNCH_END);
    const afternoonEnd = parseTimeToMinutes(BUSINESS_HOURS.END);

    const morningSlotTime = morningEnd - morningStart;
    const afternoonSlotTime = afternoonEnd - afternoonStart;

    let morningCovered = 0;
    let afternoonCovered = 0;

    for (const booking of validBookings) {
        const bookingStart = new Date(booking.startTime);
        const bookingEnd = new Date(booking.endTime);

        const startMinutes = bookingStart.getHours() * 60 + bookingStart.getMinutes();
        const endMinutes = bookingEnd.getHours() * 60 + bookingEnd.getMinutes();

        // Check morning slot coverage
        if (startMinutes < morningEnd && endMinutes > morningStart) {
            const effectiveStart = Math.max(startMinutes, morningStart);
            const effectiveEnd = Math.min(endMinutes, morningEnd);
            morningCovered = Math.max(morningCovered, effectiveEnd - effectiveStart);
        }

        // Check afternoon slot coverage
        if (startMinutes < afternoonEnd && endMinutes > afternoonStart) {
            const effectiveStart = Math.max(startMinutes, afternoonStart);
            const effectiveEnd = Math.min(endMinutes, afternoonEnd);
            afternoonCovered = Math.max(afternoonCovered, effectiveEnd - effectiveStart);
        }
    }

    // Consider fully booked if both morning and afternoon slots are covered
    const morningFullyCovered = morningCovered >= morningSlotTime * 0.95; // 95% threshold
    const afternoonFullyCovered = afternoonCovered >= afternoonSlotTime * 0.95; // 95% threshold

    return morningFullyCovered && afternoonFullyCovered;
}
